import { Card, CardContent, CardHeader, CardTitle } from "@bowfast/ui/components/card";
import { Progress } from "@bowfast/ui/components/progress";
import { cn } from "@bowfast/ui/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2Icon, ChevronRightIcon, CompassIcon, ShieldCheckIcon } from "lucide-react";
import { useState } from "react";
import { fadeUp, stagger } from "~/lib/animations";
import { type AssessmentResult, RESULT_CONFIG, type SkillPathCategory, TIER_CONFIG, TIER_ORDER } from "~/lib/skills";

// ── TierProgress (internal) ───────────────────────────────────────────────────

function TierProgress({ categories }: Readonly<{ categories: SkillPathCategory[] }>) {
  const allAssessments = categories.flatMap((c) => c.assessments);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-[0.9375rem] font-semibold">Progress by level</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {TIER_ORDER.map((tier) => {
          const tierAssessed = allAssessments.filter((a) => a.tier === tier);
          const tierPassed = tierAssessed.filter((a) => a.result === "passed").length;
          const tierTotal = tierAssessed.length;
          const pct = tierTotal > 0 ? Math.round((tierPassed / tierTotal) * 100) : 0;
          const cfg = TIER_CONFIG[tier];
          return (
            <div key={tier} className="flex items-center gap-3">
              <span className={cn("w-20 shrink-0 text-xs font-semibold", cfg.color)}>{cfg.label}</span>
              <Progress value={pct} className="h-1.5 flex-1" />
              <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                {tierPassed}/{tierTotal}
              </span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ── SkillPathTab ──────────────────────────────────────────────────────────────

export function SkillPathTab({ categories }: { categories: SkillPathCategory[] }) {
  const totalPassed = categories.reduce((s, c) => s + c.passed, 0);
  const totalSkills = categories.reduce((s, c) => s + c.total, 0);
  const [expandedCat, setExpandedCat] = useState<string | null>(null);

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-5">
      {/* Header stat */}
      <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
          <div className="flex size-8 items-center justify-center rounded-lg bg-shallow-50 dark:bg-shallow-900">
            <ShieldCheckIcon className="size-4 text-shallow-600" />
          </div>
          <div>
            <p className="font-heading text-xl font-bold tabular-nums leading-none">
              {totalPassed}
              <span className="text-sm font-normal text-muted-foreground">/{totalSkills}</span>
            </p>
            <p className="text-xs text-muted-foreground">skills assessed</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
          <div className="flex size-8 items-center justify-center rounded-lg bg-abyss-50 dark:bg-abyss-900">
            <CompassIcon className="size-4 text-abyss-500" />
          </div>
          <div>
            <p className="font-heading text-xl font-bold tabular-nums leading-none">
              {categories.flatMap((c) => c.assessments).length}
            </p>
            <p className="text-xs text-muted-foreground">assessments recorded</p>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Left: category cards */}
        <div className="space-y-4 lg:col-span-2">
          {categories.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                <ShieldCheckIcon className="size-8 text-muted-foreground/30" />
                <p className="font-heading text-base font-semibold">No assessments yet</p>
                <p className="text-sm text-muted-foreground">
                  Skill assessments are recorded by skippers during expeditions.
                </p>
              </CardContent>
            </Card>
          )}
          {categories.map((cat) => {
            const isExpanded = expandedCat === cat.name;
            const pct = cat.total > 0 ? Math.round((cat.passed / cat.total) * 100) : 0;
            return (
              <motion.div key={cat.name} variants={fadeUp}>
                <Card>
                  <CardHeader className="pb-3">
                    <button
                      type="button"
                      onClick={() => setExpandedCat(isExpanded ? null : cat.name)}
                      className="flex w-full items-center gap-3 text-left"
                    >
                      <div className={cn("transition-transform duration-200", isExpanded ? "rotate-90" : "rotate-0")}>
                        <ChevronRightIcon className="size-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-heading text-[0.9375rem] font-semibold leading-tight">{cat.name}</p>
                        <div className="mt-1.5 flex items-center gap-2">
                          <Progress value={pct} className="h-1.5 flex-1" />
                          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                            {cat.passed}/{cat.total}
                          </span>
                        </div>
                      </div>
                    </button>
                  </CardHeader>

                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        key="body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                        style={{ overflow: "hidden" }}
                      >
                        <CardContent className="space-y-0 p-0">
                          {cat.assessments.map((a) => {
                            const tierCfg = TIER_CONFIG[a.tier];
                            const resCfg = RESULT_CONFIG[a.result];
                            return (
                              <div key={a.id} className="flex items-start gap-3 border-t border-border px-4 py-3">
                                <CheckCircle2Icon className="mt-0.5 size-4 shrink-0 text-shallow-500" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium">{a.skillName}</p>
                                  <p className="mt-0.5 text-xs text-muted-foreground">
                                    Assessed by {a.assessor} · {a.expedition} · {a.date}
                                  </p>
                                  {a.notes && <p className="mt-1 text-xs italic text-muted-foreground">"{a.notes}"</p>}
                                </div>
                                <div className="flex shrink-0 flex-col items-end gap-1.5">
                                  <span
                                    className={cn(
                                      "rounded-full px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-[0.04em]",
                                      resCfg.className,
                                    )}
                                  >
                                    {resCfg.label} · {a.ratingLabel}
                                  </span>
                                  <span
                                    className={cn(
                                      "text-[0.625rem] font-semibold uppercase tracking-[0.04em]",
                                      tierCfg.color,
                                    )}
                                  >
                                    {tierCfg.label}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </CardContent>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Right: tier progress + legend */}
        <div className="space-y-4">
          <motion.div variants={fadeUp}>
            <TierProgress categories={categories} />
          </motion.div>
          <motion.div variants={fadeUp}>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-[0.9375rem] font-semibold">About assessments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                {(["passed", "failed", "partial"] as AssessmentResult[]).map((r) => {
                  const cfg = RESULT_CONFIG[r];
                  return (
                    <div key={r} className="flex items-center gap-2">
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-[0.04em]",
                          cfg.className,
                        )}
                      >
                        {cfg.label}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {r === "passed"
                          ? "Skill signed off by skipper or instructor"
                          : r === "failed"
                            ? "Needs further practice"
                            : "Partially demonstrated"}
                      </span>
                    </div>
                  );
                })}
                <p className="mt-3 text-xs text-muted-foreground">
                  Assessments are recorded per expedition. Skippers and instructors with SKIPPER role or above can
                  record assessments.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
